import type { Locale } from "../content/types";
import { useLocation } from "react-router-dom";
import { landingByLocale, privacyByLocale, termsByLocale } from "../content";
import { luxoriaConfig } from "../config";
import { PageMeta } from "../components/PageMeta";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Container } from "../components/Container";
import { BrandMark } from "../components/BrandMark";

type LegalPageProps = {
  locale: Locale;
  kind: "privacy" | "terms";
};

export function LegalPage({ locale, kind }: LegalPageProps) {
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const contentOnly = query.get("embed") === "1";
  const landing = landingByLocale[locale];
  const content = kind === "privacy" ? privacyByLocale[locale] : termsByLocale[locale];

  return (
    <>
      <PageMeta title={content.seo.title} description={content.seo.description} locale={locale} kind={kind} />
      {contentOnly ? null : <Header locale={locale} kind={kind} nav={landing.nav} variant="minimal" />}
      <main className="bg-bg-primary py-16 lg:py-20">
        <Container width="legal">
          <div className="flex flex-col gap-4">
            <div className="inline-flex w-fit items-center gap-2.5 rounded-full border border-subtle bg-bg-card px-3 py-1.5">
              <BrandMark size={22} />
              <span className="font-display text-sm font-semibold text-text-primary">Luxoria</span>
            </div>
            <h1 className="font-display text-4xl font-bold text-text-primary sm:text-[44px]">{content.title}</h1>
            <p className="text-sm text-text-muted">{content.lastUpdated}</p>
          </div>

          <div className="mt-10 flex flex-col gap-7 rounded-3xl border border-subtle bg-bg-card p-7 sm:p-11">
            {content.sections.map((section) => (
              <section key={section.heading} className="flex flex-col gap-2">
                <h2 className="font-display text-base font-semibold text-text-primary">{section.heading}</h2>
                <p className="text-sm leading-relaxed text-text-secondary">{section.body}</p>
              </section>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-4 rounded-3xl border border-subtle bg-gradient-to-br from-[#13203A] to-bg-card p-7">
            <div className="flex flex-col gap-1">
              {contentOnly ? (
                <div className="inline-flex items-center gap-2">
                  <BrandMark size={18} />
                  <span className="font-display text-sm font-semibold text-text-primary">Luxoria</span>
                </div>
              ) : null}
              <h2 className="font-display text-base font-semibold text-text-primary">{content.contact.title}</h2>
              <p className="text-sm text-text-secondary">
                {content.contact.body.split(luxoriaConfig.contactEmail)[0]}
                <a href={`mailto:${luxoriaConfig.contactEmail}`} className="font-medium text-accent-blue hover:underline">
                  {luxoriaConfig.contactEmail}
                </a>
                {content.contact.body.split(luxoriaConfig.contactEmail)[1]}
              </p>
            </div>
          </div>
        </Container>
      </main>
      {contentOnly ? null : <Footer locale={locale} kind={kind} content={landing.footer} />}
    </>
  );
}
